import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { msg } from "@lingui/core/macro"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  ConversationMessageSenderKind,
  DealDrawerActivityFilter,
  DealDrawerActivityItem,
  DealDrawerActivityKind,
} from "@/types/v2/conversation"
import { Shimmer } from "@shimmer-from-structure/react"
import {
  EMPTY_STATE_COPY,
  countActivity,
  filterActivity,
} from "@/lib/deal-activity-filter"
import { buildActivityShimmerItems } from "@/lib/activity-shimmer-template"
import { scrollFadeMask, useScrollFade } from "@/hooks/use-scroll-fade"
import {
  type ActiveFacet,
  extractFacetGroups,
  filterByFacet,
} from "@/lib/activity-facets"
import { OutboundSenderAvatar } from "@/components/v2/messages/outbound-sender-avatar"
import { CallRowBody, CallRowIcon } from "@/components/v2/voice/call-row"
import { DeleteConfirmKebab } from "@/components/v2/delete/delete-confirm-kebab"
import { deleteRow } from "@/lib/use-row-delete"
import {
  NoteComposer,
  type NoteComposerEmployee,
} from "@/components/v2/deals/note-composer"
import { TimelineSearchField } from "@/components/v2/deals/timeline-search-field"
import { ActivityFacetRow } from "@/components/v2/deals/activity-facet-row"

// Shared activity timeline for every deal / contact
// surface: the deal drawer (slideover), the full deal
// show page, the contact drawer, and the contact show
// page. Both group items by relative day on the client
// because the server can't know the user's local timezone
// with any accuracy.
//
// Both variants render the "All / Messages / Notes /
// Calls / Updates" chip strip — the slideover (Paper
// P9Q-0) gets the same filter affordance as the show page
// (Paper K2D-1), differing only in heading style and row
// density. Counts come from the unfiltered list so the
// chip badges stay stable when a tab is active.
//
// Filtering is client-side (against the `items` prop), so
// every kind is always present in the payload — the
// Calls and Updates chips can never be starved by a long
// run of messages the way a server-paginated feed could.
//
// Note composer: when a deal context (`deal`) AND a
// `currentEmployee` are supplied, the heading row grows an
// "Add note" affordance. Desktop (`composerVariant:
// "inline"`) expands an inline `NoteComposer` under the
// header; mobile (`composerVariant: "external"`) calls
// `onAddNoteClick` so the parent can hoist a bottom sheet.
// The composer is gated on `deal && currentEmployee`, so
// the contact feeds (which pass neither) never render it.

interface ActivityFeedProps {
  items: DealDrawerActivityItem[]
  // The full page uses a wider, lane-style timeline
  // (Paper GE7-1) — icon column with a vertical rule,
  // larger note callouts, h2 heading. The drawer uses
  // the tighter inline style with an uppercase SECTION
  // label. Both render the filter chip strip.
  variant?: "drawer" | "page"
  // Heading shown above (drawer) or beside (page) the
  // filter chips. The page wraps this in an <h2> styled
  // as a heading; the drawer renders it as-is so the
  // caller can pass a SECTION label component. Pass
  // plain text or text-shaped nodes — not other heading
  // levels.
  heading?: React.ReactNode
  // Trailing affordance shown to the right of the
  // heading row on the page variant (e.g. an extra
  // button). Rendered alongside the built-in "Add note"
  // affordance. The drawer ignores this prop.
  actions?: React.ReactNode
  // The outer scroll container that owns vertical
  // scroll for the surface this feed renders inside
  // (the drawer body, or the deal show column). The
  // shared scroll memory keeps this element's scrollTop
  // in place across filter changes. Optional — when
  // absent, chip memory is a no-op.
  scrollContainer?: HTMLElement | null
  // Note-composer context. The "Add note" affordance +
  // composer only render when BOTH `deal` and
  // `currentEmployee` are present — i.e. the deal show
  // page and the deal slideover. Contact feeds omit both,
  // so they get the read-only timeline.
  deal?: { id: number; name: string }
  currentEmployee?: NoteComposerEmployee | null
  // "inline" (desktop) expands the composer in place;
  // "external" (mobile) delegates to `onAddNoteClick` so
  // the parent can open a bottom sheet.
  composerVariant?: "inline" | "external"
  onAddNoteClick?: () => void
  // When true, the TIMELINE ROWS are replaced with a
  // structure-matched shimmer (via shimmer-from-structure)
  // while the deferred `activity` prop is in flight. Only
  // the rows shimmer — the heading and the filter chip
  // labels are static chrome and render immediately. The
  // chip count badges are derived from `items`, so they're
  // simply absent until the data lands (counts pop in). The
  // contact show is the only surface that defers; every
  // other feed is eager and leaves this false.
  loading?: boolean
  // How many shimmer rows to render while `loading` — set to
  // the feed's real (eagerly-counted) row count so the
  // shimmer matches the loaded feed's height and nothing
  // shifts. Ignored unless `loading`.
  loadingRowCount?: number
  // Timeline search (CON-146). When supplied, the feed renders
  // the search field at the very top (above the kind chips).
  // The feed owns the input's value + debounce (so it survives
  // partial reloads); the SURFACE owns the reload via
  // `onSearch`. `initialValue` seeds the input from the URL's
  // `activity_search` param on first mount. Omit to hide the
  // search field (e.g. the not-yet-search surfaces).
  search?: ActivitySearchConfig
  // Event-facet row (CON-146) — DEAL feeds only. When true,
  // the feed renders the data-driven facet pills between the
  // kind chips and the rows, and filters event rows
  // client-side by the active facet. Contact feeds leave this
  // false (they keep their scope pills instead).
  facets?: boolean
  // Note-row delete (CON-253). When supplied, note rows grow
  // a hover-revealed kebab that confirms + DELETEs the note,
  // then calls this so the HOST reloads the correct feed prop
  // (deal surfaces reload `activity`; the contact show reloads
  // its deferred `activity` manifest — the prop name differs
  // by surface, so each host owns its own reload). Omit on
  // read-only feeds that can't refresh after a delete.
  onNoteDeleted?: () => void
}

export interface ActivitySearchConfig {
  // Initial search text from the URL's `activity_search`
  // param (read once on mount; the input owns it after).
  initialValue?: string
  // Debounced callback the surface turns into a partial reload
  // — with `activity_search` when non-empty, without it when
  // empty.
  onSearch: (value: string) => void
}

export function ActivityFeed({
  items,
  variant = "drawer",
  heading,
  actions,
  scrollContainer,
  deal,
  currentEmployee,
  composerVariant = "inline",
  onAddNoteClick,
  loading = false,
  loadingRowCount = 0,
  search,
  facets = false,
  onNoteDeleted,
}: ActivityFeedProps) {
  const { i18n } = useLingui()
  const [filter, setFilter] = useState<DealDrawerActivityFilter>("all")
  // Active event-facet selection (deal feeds only). Client-
  // side, like the kind filter — narrows event rows to the
  // chosen field+value while leaving messages/notes/calls
  // untouched.
  const [activeFacet, setActiveFacet] = useState<ActiveFacet | null>(null)

  // Facet groups are derived from the loaded items. In server-
  // search mode the items carry no events, so this is empty and
  // the facet row renders nothing — no stale/disabled pills to
  // reason about. Only computed when `facets` is on.
  const facetGroups = useMemo(
    () => (facets ? extractFacetGroups(items, i18n) : []),
    [facets, items, i18n],
  )

  // If the active facet's field/value vanishes from the loaded
  // items (a search narrowed the feed, or the user cleared the
  // matching event), drop the selection so the feed doesn't
  // silently hide every event row against a facet that's no
  // longer offered.
  const facetStillPresent =
    activeFacet != null &&
    facetGroups.some(
      (group) =>
        group.field === activeFacet.field &&
        group.values.some((v) => v.toLabel === activeFacet.toLabel),
    )
  const effectiveFacet = facetStillPresent ? activeFacet : null

  // Counts come from the FACET-narrowed list so the kind-chip
  // badges agree with what an active facet actually shows. With
  // no active facet this is just `items`.
  const facetedItems = useMemo(
    () => filterByFacet(items, effectiveFacet),
    [items, effectiveFacet],
  )
  const counts = useMemo(() => countActivity(facetedItems), [facetedItems])
  const visibleItems = useMemo(
    () => filterActivity(facetedItems, filter),
    [facetedItems, filter],
  )

  // The composer only exists where a deal context and the
  // current employee are both known (deal show + deal
  // slideover). Contact feeds pass neither.
  const composerEnabled = Boolean(deal && currentEmployee)
  const [composerOpen, setComposerOpen] = useState(false)
  const inlineComposerActive =
    composerEnabled && composerVariant === "inline" && composerOpen

  const onAddNoteButtonClick = () => {
    if (composerVariant === "external") {
      onAddNoteClick?.()
      return
    }
    setComposerOpen(true)
  }

  // The "Add note" affordance hides while the inline
  // composer is open (the composer owns the surface until
  // Save / Cancel).
  const addNoteButton =
    composerEnabled && !inlineComposerActive ? (
      <AddNoteButton onClick={onAddNoteButtonClick} />
    ) : null

  const inlineComposer =
    inlineComposerActive && deal && currentEmployee ? (
      <NoteComposer
        deal={deal}
        currentEmployee={currentEmployee}
        autoFocus
        onSaved={() => setComposerOpen(false)}
        onCancel={() => setComposerOpen(false)}
      />
    ) : null

  // The chip strip hides while the inline composer is
  // open, so the composer owns the heading row.
  const showChips = !inlineComposerActive

  // Search field (top of the feed, above the kind chips) —
  // only when the surface wired a `search` config. Stays
  // visible while the inline composer is open: it's the
  // feed's top-level affordance, independent of note-taking.
  const searchNode = search ? (
    <TimelineSearchField
      initialValue={search.initialValue}
      onSearch={search.onSearch}
    />
  ) : null

  // Event-facet row (deal feeds only). Sits with the kind
  // chips — so it hides alongside them while the composer
  // owns the row, and renders nothing when no event carries a
  // facet (e.g. server-search mode, or a feed with no
  // changes).
  const facetNode =
    facets && showChips ? (
      <ActivityFacetRow
        groups={facetGroups}
        active={effectiveFacet}
        onChange={setActiveFacet}
      />
    ) : null

  // Single shared scroll memory (CON-113 → CON-119 →
  // CON-225). The natural flow is "scroll down to the
  // history section, then click around the chips" — so a
  // chip switch must keep the OUTER scroll container
  // exactly where the user left it. ONE remembered
  // position is shared across every chip: no per-chip
  // positions, and never a jump back to the top for a
  // never-visited chip.
  //
  // When a short filter (e.g. Notes with one row) shrinks
  // the content, the browser clamps the scroll upward.
  // That clamped detour must not overwrite the user's
  // spot — we only adopt the current scrollTop as the
  // remembered position when it differs from what our
  // last restore landed on (i.e. the user actually
  // scrolled). Switching back to a longer chip then
  // returns to the remembered position.
  //
  // The container is owned by the parent surface (drawer
  // body or deal-show column). When `scrollContainer` is
  // null/undefined (jsdom tests, the rare full-page
  // activity surface), memory is a no-op and the filter
  // just swaps content.
  const rememberedScrollRef = useRef<number | null>(null)
  const lastRestoredScrollRef = useRef<number | null>(null)

  const onFilterChange = (next: DealDrawerActivityFilter) => {
    if (next === filter) return
    if (scrollContainer) {
      const current = scrollContainer.scrollTop
      const userScrolledSinceRestore =
        current !== lastRestoredScrollRef.current
      if (
        rememberedScrollRef.current === null ||
        userScrolledSinceRestore
      ) {
        rememberedScrollRef.current = current
      }
    }
    setFilter(next)
  }

  useLayoutEffect(() => {
    const remembered = rememberedScrollRef.current
    if (remembered === null || !scrollContainer) return
    scrollContainer.scrollTop = remembered
    // Read back the post-clamp position so the next chip
    // click can tell "browser clamped a short filter"
    // apart from "user scrolled".
    lastRestoredScrollRef.current = scrollContainer.scrollTop
  }, [filter, scrollContainer])

  if (variant === "page") {
    const header = (
      <PageActivityHeader
        heading={heading}
        actions={actions}
        addNote={addNoteButton}
        showChips={showChips}
        counts={counts}
        filter={filter}
        onFilterChange={onFilterChange}
      />
    )

    // While loading, feed the rows mock items so the shimmer
    // has real structure to measure — filtered by the active
    // chip so switching tabs mid-load stays consistent.
    // Otherwise render the actual filtered items. Only this
    // rows node is wrapped in <Shimmer> — the header (heading
    // + chip labels) above stays static.
    const rows = (
      <PageActivityRows
        items={loadingRowItems(loading, visibleItems, filter, loadingRowCount)}
        filter={filter}
        onNoteDeleted={onNoteDeleted}
      />
    )
    return (
      <div className="flex flex-col gap-4">
        {searchNode}
        {header}
        {facetNode}
        {inlineComposer}
        {loading ? <ActivityRowsShimmer>{rows}</ActivityRowsShimmer> : rows}
      </div>
    )
  }

  // Drawer variant: pills inline, no internal scroll.
  // The parent drawer body owns vertical scroll, and the
  // user reaches the activity feed by scrolling the
  // whole drawer. Chip switches keep the drawer body's
  // scrollTop in place via `scrollContainer`.
  //
  // As in the page variant, only the rows shimmer while
  // loading — heading + chip labels stay static.
  const rows = (
    <DrawerActivityRows
      items={loadingRowItems(loading, visibleItems, filter, loadingRowCount)}
      filter={filter}
      onNoteDeleted={onNoteDeleted}
    />
  )
  return (
    <div className="flex flex-col gap-4">
      {heading || addNoteButton ? (
        <div className="flex items-center gap-2">
          {heading ? <div className="min-w-0">{heading}</div> : null}
          <div className="grow" />
          {addNoteButton}
        </div>
      ) : null}
      {searchNode}
      {inlineComposer}
      {showChips ? (
        <FilterChipGroup
          counts={counts}
          filter={filter}
          onFilterChange={onFilterChange}
        />
      ) : null}
      {facetNode}
      {loading ? <ActivityRowsShimmer>{rows}</ActivityRowsShimmer> : rows}
    </div>
  )
}

// Rows to render while loading: builds `loadingRowCount`
// shimmer template rows, then filters them by the active
// chip. On the default "all" filter that's exactly
// `loadingRowCount` rows — so the shimmer matches the real
// feed's height with no layout shift; a non-"all" filter may
// be shorter (it only counts that kind), which is fine since
// the real filtered feed is also shorter. Returns the real
// visible items once loaded.
function loadingRowItems(
  loading: boolean,
  visibleItems: DealDrawerActivityItem[],
  filter: DealDrawerActivityFilter,
  loadingRowCount: number,
): DealDrawerActivityItem[] {
  if (!loading) return visibleItems
  return filterActivity(buildActivityShimmerItems(loadingRowCount), filter)
}

// Wraps the timeline rows in the structure-matched shimmer
// while activity is loading. The `data-testid` is an
// app-owned, stable hook so tests assert loading on OUR
// markup rather than the shimmer library's internal
// `.shimmer-measure-container` class (which can change on a
// dependency bump).
function ActivityRowsShimmer({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <div data-testid="activity-feed-loading">
      <Shimmer loading>{children}</Shimmer>
    </div>
  )
}

// Day-grouped timeline rows for the page variant, or the
// filter's empty-state copy when there are none. Extracted
// so the loading shimmer can wrap exactly these rows (fed
// mock items) without shimmering the heading + chips above.
function PageActivityRows({
  items,
  filter,
  onNoteDeleted,
}: {
  items: DealDrawerActivityItem[]
  filter: DealDrawerActivityFilter
  onNoteDeleted?: () => void
}) {
  const { t } = useLingui()
  if (items.length === 0) {
    return (
      <div className="text-[#7A746B] text-[13px] leading-5">
        {EMPTY_STATE_COPY[filter]}
      </div>
    )
  }
  const groups = groupByDay(items, t)
  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col">
          <DayDivider label={group.label} />
          {group.items.map((item, index) => (
            <PageActivityRow
              key={item.id}
              item={item}
              isLast={
                index === group.items.length - 1 &&
                group === groups[groups.length - 1]
              }
              onNoteDeleted={onNoteDeleted}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// Day-grouped timeline rows for the drawer variant (tighter
// spacing, no lane rule). Same extraction rationale as
// `PageActivityRows`.
function DrawerActivityRows({
  items,
  filter,
  onNoteDeleted,
}: {
  items: DealDrawerActivityItem[]
  filter: DealDrawerActivityFilter
  onNoteDeleted?: () => void
}) {
  const { t } = useLingui()
  if (items.length === 0) {
    return (
      <div className="text-[#7A746B] text-[13px] leading-5">
        {EMPTY_STATE_COPY[filter]}
      </div>
    )
  }
  const groups = groupByDay(items, t)
  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-3">
          <div className="tracking-[0.06em] uppercase font-medium text-[#9A938A] text-[11px] leading-3">
            {group.label}
          </div>
          {group.items.map((item) => (
            <DrawerActivityRow
              key={item.id}
              item={item}
              onNoteDeleted={onNoteDeleted}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

interface PageActivityHeaderProps {
  heading: React.ReactNode
  actions: React.ReactNode
  addNote: React.ReactNode
  showChips: boolean
  counts: ReturnType<typeof countActivity>
  filter: DealDrawerActivityFilter
  onFilterChange: (next: DealDrawerActivityFilter) => void
}

function PageActivityHeader({
  heading,
  actions,
  addNote,
  showChips,
  counts,
  filter,
  onFilterChange,
}: PageActivityHeaderProps) {
  return (
    <div className="flex items-baseline gap-3 flex-wrap">
      <h2 className="tracking-[-0.01em] font-semibold text-foreground text-base leading-5">
        {heading ?? <Trans>Activity</Trans>}
      </h2>
      {showChips ? (
        <FilterChipGroup
          counts={counts}
          filter={filter}
          onFilterChange={onFilterChange}
        />
      ) : null}
      <div className="grow" />
      {actions}
      {addNote}
    </div>
  )
}

// The "Add note" pill rendered in the feed's heading row
// when a deal context + current employee are present.
// Keeps the `history-add-note` testid that the deal-show
// surfaces already assert against.
function AddNoteButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="history-add-note"
      className="flex items-center gap-1.5 rounded-full border border-border bg-background text-foreground text-[12px] leading-4 px-3 py-1 font-medium hover:bg-muted"
    >
      <AddNotePlusIcon />
      <Trans>Add note</Trans>
    </button>
  )
}

function AddNotePlusIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 3V13M3 8H13"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

interface FilterChipGroupProps {
  counts: ReturnType<typeof countActivity>
  filter: DealDrawerActivityFilter
  onFilterChange: (next: DealDrawerActivityFilter) => void
}

function FilterChipGroup({
  counts,
  filter,
  onFilterChange,
}: FilterChipGroupProps) {
  const { t } = useLingui()
  // We use a toggle-button group with `aria-pressed`
  // rather than a `tablist`/`tab` pattern. The full WAI-
  // ARIA tabs pattern expects keyboard arrow-navigation
  // and a paired `tabpanel` with `aria-controls`; this
  // feed has neither — the chips just filter the same
  // visible list. `aria-pressed` accurately describes
  // what the chips actually do.
  //
  // The strip scrolls horizontally rather than wrapping or
  // overflowing the panel (the contact slideover is narrow
  // on mobile). A `mask-image` edge fade signals there's
  // more to reveal — without it the scroll is invisible on
  // iOS, which has no persistent scrollbar.
  const { ref, fadeStart, fadeEnd } = useScrollFade()

  return (
    <div
      ref={ref}
      className="flex min-w-0 gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      style={{ maskImage: scrollFadeMask({ fadeStart, fadeEnd }) }}
      role="group"
      aria-label={t`Activity filters`}
    >
      <FilterChip
        label={t`All`}
        count={counts.all}
        active={filter === "all"}
        onClick={() => onFilterChange("all")}
      />
      <FilterChip
        label={t`Messages`}
        count={counts.messages}
        active={filter === "messages"}
        onClick={() => onFilterChange("messages")}
      />
      <FilterChip
        label={t`Notes`}
        count={counts.notes}
        active={filter === "notes"}
        onClick={() => onFilterChange("notes")}
      />
      <FilterChip
        label={t`Calls`}
        count={counts.calls}
        active={filter === "calls"}
        onClick={() => onFilterChange("calls")}
      />
      <FilterChip
        label={t`Updates`}
        count={counts.updates}
        active={filter === "updates"}
        onClick={() => onFilterChange("updates")}
      />
    </div>
  )
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`flex shrink-0 items-center rounded-full py-1 px-2.75 gap-1.5 text-xs leading-4 cursor-pointer ${
        active
          ? "bg-[#ECE7DE] font-medium text-foreground"
          : "text-[#7A746B] hover:text-foreground hover:bg-[#F7F5F0]"
      }`}
    >
      <span>{label}</span>
      {count > 0 ? (
        <span
          className={`tabular-nums text-[10px] leading-3 ${
            active ? "text-[#5C544A]" : "text-[#9A938A]"
          }`}
        >
          {count}
        </span>
      ) : null}
    </button>
  )
}

function DayDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center pt-1.5 pb-3.5 gap-2.5">
      <div className="tracking-[0.08em] uppercase font-medium text-[#9A938A] text-[10px] leading-3">
        {label}
      </div>
      <div className="grow h-px bg-[#E5DFD3]" />
    </div>
  )
}

function DrawerActivityRow({
  item,
  onNoteDeleted,
}: {
  item: DealDrawerActivityItem
  onNoteDeleted?: () => void
}) {
  const { t } = useLingui()
  const isNote = item.kind === "note"
  const isMessage = item.kind === "message"
  const isCall = item.kind === "call"
  return (
    <div className="group/feedrow flex gap-3">
      <ActivityIcon item={item} />
      <div className="flex flex-col grow min-w-0 gap-1">
        <div className="flex items-baseline gap-2">
          <div className="font-medium text-[#1E1B17] text-[13px] leading-4 min-w-0">
            {item.title}
          </div>
          <div className="text-[#9A938A] text-[11px] leading-[14px]">
            {formatTime(item.occurredAtIso, t)}
          </div>
          <NoteDeleteKebab item={item} onNoteDeleted={onNoteDeleted} />
        </div>
        {isCall ? (
          <CallRowBody
            outcome={item.outcome ?? null}
            durationLabel={item.durationLabel ?? null}
            body={item.body}
            analysisInFlight={item.analysisInFlight ?? null}
            voiceCall={item.voiceCall ?? null}
          />
        ) : item.body ? (
          isNote ? (
            <div className="rounded-lg py-2.5 px-3 bg-[#FBF8F1] border border-[#F0E8D6] text-[#1E1B17] text-[13px] leading-[18px] whitespace-pre-wrap wrap-anywhere">
              {item.body}
            </div>
          ) : isMessage ? (
            <MessageBubble item={item} compact />
          ) : (
            <div className="text-[#5C544A] text-[13px] leading-[18px] whitespace-pre-wrap wrap-anywhere">
              {item.body}
            </div>
          )
        ) : null}
      </div>
    </div>
  )
}

// Page variant has a wider gutter and runs a vertical
// rule down the icon lane between rows so the timeline
// reads as a continuous track. The last row drops its
// rule.
function PageActivityRow({
  item,
  isLast,
  onNoteDeleted,
}: {
  item: DealDrawerActivityItem
  isLast: boolean
  onNoteDeleted?: () => void
}) {
  const { t } = useLingui()
  const isNote = item.kind === "note"
  const isMessage = item.kind === "message"
  const isCall = item.kind === "call"
  return (
    <div className="group/feedrow flex py-2.5 gap-3.5">
      <div className="flex flex-col items-center shrink-0 w-6 gap-1">
        <ActivityIcon item={item} />
        {isLast ? null : <div className="grow w-px bg-[#E5DFD3]" />}
      </div>
      <div className="flex flex-col grow min-w-0 pb-2 gap-1">
        <div className="flex items-baseline gap-2">
          <div className="font-medium text-[#1E1B17] text-[13px] leading-4 min-w-0">
            {item.title}
          </div>
          <div className="text-[#9A938A] text-[11px] leading-[14px]">
            {formatTime(item.occurredAtIso, t)}
          </div>
          <NoteDeleteKebab item={item} onNoteDeleted={onNoteDeleted} />
        </div>
        {isCall ? (
          <CallRowBody
            outcome={item.outcome ?? null}
            durationLabel={item.durationLabel ?? null}
            body={item.body}
            analysisInFlight={item.analysisInFlight ?? null}
            voiceCall={item.voiceCall ?? null}
          />
        ) : item.body ? (
          isNote ? (
            <div className="rounded-lg py-2.5 px-3 bg-[#FFFCEB] border border-[#F0E8C9] text-[#1E1B17] text-[13px] leading-[19px] whitespace-pre-wrap wrap-anywhere">
              {item.body}
            </div>
          ) : isMessage ? (
            <MessageBubble item={item} />
          ) : (
            <div className="text-[#7A746B] text-[13px] leading-[19px] whitespace-pre-wrap wrap-anywhere">
              {item.body}
            </div>
          )
        ) : null}
      </div>
    </div>
  )
}

// DELETE endpoint for a feed note row (CON-253). Deal-anchored
// notes route through the deal's notes resource; contact-only
// notes through the contact's. Returns null when the item
// isn't a deletable note (no `noteId`, or no owning record id)
// so the caller can skip the kebab entirely.
function noteDeleteUrl(item: DealDrawerActivityItem): string | null {
  if (item.kind !== "note" || item.noteId == null) return null
  if (item.dealId != null) {
    return `/v2/deals/${item.dealId}/notes/${item.noteId}`
  }
  if (item.contactId != null) {
    return `/v2/contacts/${item.contactId}/notes/${item.noteId}`
  }
  return null
}

// Hover-revealed (desktop) / always-visible (mobile) delete
// affordance on note rows. Renders nothing unless the host
// wired `onNoteDeleted` AND the note carries the ids needed
// to build its DELETE URL — so read-only feeds (which can't
// refresh after a delete) never grow a dead kebab. The
// confirm + request + toast all live in the shared
// `DeleteConfirmKebab` + `deleteRow`; the host owns the
// feed reload via `onNoteDeleted`.
function NoteDeleteKebab({
  item,
  onNoteDeleted,
}: {
  item: DealDrawerActivityItem
  onNoteDeleted?: () => void
}) {
  const url = noteDeleteUrl(item)
  if (!onNoteDeleted || !url) return null
  return (
    <div className="ml-auto shrink-0 self-center">
      <DeleteConfirmKebab
        promptKind="note"
        quote={item.body}
        ariaLabel="Note options"
        testId={`feed-note-delete-${item.id}`}
        triggerClassName="md:opacity-0 md:group-hover/feedrow:opacity-100 md:aria-expanded:opacity-100"
        onConfirmDelete={async () => {
          const ok = await deleteRow(url, "Note deleted")
          if (ok) onNoteDeleted()
          return ok
        }}
      />
    </div>
  )
}

// Inbound and outbound messages get visually distinct
// chat-bubble treatments so the timeline reads like a
// transcript when filtered to "Messages". Inbound bubbles
// use the warm-cream contact tone; outbound use the cool
// neutral team tone.
function MessageBubble({
  item,
  compact = false,
}: {
  item: DealDrawerActivityItem
  compact?: boolean
}) {
  const inbound = item.direction === "inbound"
  const padding = compact ? "py-2 px-3" : "py-2.5 px-3"
  const inboundStyle =
    "bg-[#FBF6EE] border-[#F0E5D2] text-[#1E1B17]"
  const outboundStyle =
    "bg-[#F2F0EA] border-[#E0DAC9] text-[#1E1B17]"
  return (
    <div
      className={`rounded-lg border text-[13px] leading-[18px] whitespace-pre-wrap wrap-anywhere ${padding} ${
        inbound ? inboundStyle : outboundStyle
      }`}
    >
      {item.body}
    </div>
  )
}

const ICON_BG: Record<DealDrawerActivityKind, string> = {
  created: "bg-[#F0EDE6]",
  stage: "bg-[#F0EDE6]",
  value: "bg-[#FAF3EB]",
  status: "bg-[#EAF2E5]",
  name: "bg-[#F0EDE6]",
  assignment: "bg-[#F0EDE6]",
  activity: "bg-[#F0EDE6]",
  note: "bg-[#1E1B17]",
  message: "bg-[#E8E2D2]",
  call: "bg-[#E7EEF2]",
}

function ActivityIcon({ item }: { item: DealDrawerActivityItem }) {
  const { t } = useLingui()
  if (item.kind === "call") {
    return (
      <CallRowIcon
        outcome={item.outcome ?? null}
        direction={item.direction}
      />
    )
  }
  if (item.kind === "note") {
    return (
      <div
        // Treat the whole circle as one shimmer block so the
        // charcoal background doesn't leak around the inner
        // glyph as a ring while loading.
        data-shimmer-no-children
        className={`flex items-center justify-center shrink-0 rounded-full size-6 ${ICON_BG.note}`}
      >
        <div className="font-semibold text-[#F0EDE6] text-[9px] leading-3">
          {item.actor.initials || "AI"}
        </div>
      </div>
    )
  }
  if (item.kind === "message") {
    // Outbound messages carry the sender's identity in the
    // actor.kind — render the matching avatar so WhatsApp
    // Business App messages, AI replies, and employee
    // replies are visually distinguishable in the timeline.
    if (item.direction === "outbound") {
      const senderKind = actorKindToSenderKind(item.actor.kind)
      if (senderKind) {
        return (
          <OutboundSenderAvatar
            senderKind={senderKind}
            initials={item.actor.initials}
            size="compact"
          />
        )
      }
    }

    return (
      <div
        data-shimmer-no-children
        className={`flex items-center justify-center shrink-0 rounded-full size-6 ${ICON_BG.message}`}
        aria-label={
          item.direction === "inbound"
            ? t`Incoming message`
            : t`Outgoing message`
        }
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M2.5 5.5C2.5 4.39543 3.39543 3.5 4.5 3.5H11.5C12.6046 3.5 13.5 4.39543 13.5 5.5V9.5C13.5 10.6046 12.6046 11.5 11.5 11.5H7L4 13.5V11.5H4.5C3.39543 11.5 2.5 10.6046 2.5 9.5V5.5Z"
            stroke="#1E1B17"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    )
  }
  return (
    <div
      data-shimmer-no-children
      className={`flex items-center justify-center shrink-0 rounded-full size-6 ${ICON_BG[item.kind] ?? ICON_BG.created}`}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M6 4L10 8L6 12"
          stroke="#1E1B17"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

interface DayGroup {
  label: string
  items: DealDrawerActivityItem[]
}

function groupByDay(
  items: DealDrawerActivityItem[],
  t: ReturnType<typeof useLingui>["t"],
): DayGroup[] {
  const groups = new Map<string, DealDrawerActivityItem[]>()
  for (const item of items) {
    const label = relativeDayLabel(item.occurredAtIso, t)
    const bucket = groups.get(label) ?? []
    bucket.push(item)
    groups.set(label, bucket)
  }
  return Array.from(groups.entries()).map(([label, items]) => ({
    label,
    items,
  }))
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

function relativeDayLabel(
  iso: string,
  t: ReturnType<typeof useLingui>["t"],
): string {
  const now = new Date()
  const date = new Date(iso)
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  )
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  )
  const diffDays = Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / MS_PER_DAY,
  )

  if (diffDays === 0) return t(msg`Today`)
  if (diffDays === 1) return t(msg`Yesterday`)

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date)
}

function formatTime(
  iso: string,
  t: ReturnType<typeof useLingui>["t"],
): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.round(diffMs / 60000)

  if (diffMin < 1) return t(msg`just now`)
  if (diffMin < 60) return t(msg`${diffMin}m ago`)

  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (sameDay) {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }).format(date)
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date)
}

// Bridges the activity-feed actor kind to the avatar's
// sender kind. Contacts never appear here (inbound icons
// take a different branch), so we map the outbound-only
// kinds.
function actorKindToSenderKind(
  actorKind: DealDrawerActivityItem["actor"]["kind"],
): ConversationMessageSenderKind | null {
  switch (actorKind) {
    case "employee":
      return "employee"
    case "ai":
      return "ai"
    case "whatsapp_business_app":
      return "whatsapp_business_app"
    default:
      return null
  }
}
